import apiClient from "@/lib/axios";

// Types
export interface CatalogItem {
    id: string | number;
    label: string;
    slug?: string;
    icon?: string;
}

export interface CatalogResponse {
    items: CatalogItem[];
}

export interface PlaceResult {
    id?: string | number;
    name?: string;
    destination?: string;
    latitude?: number;
    longitude?: number;
    country_code?: string;
    lat?: number;
    lng?: number;
}

export interface CreatePlanPayload {
    travel_type: string;
    budget_type: string;
    person_count: number;
    start_date: string;
    country_code: string;
    end_date: string;
    destination: string;
    latitude: number | null;
    longitude: number | null;
}

export interface CreatePlanResponse {
    plan_id: string;
    slug: string;
    message?: string;
}

// APIs
export const getTravelTypes = async (language: string): Promise<CatalogResponse> => {
    const response = await apiClient.get("/api/v1/catalog/travel-types", {
        params: {
            language,
        },
    });
    return response.data;
};

export const getBudgets = async (language: string): Promise<CatalogResponse> => {
    const response = await apiClient.get("/api/v1/catalog/budgets", {
        params: {
            language,
        },
    });
    return response.data;
};

export const searchPlaces = async (query: string): Promise<PlaceResult[]> => {
    if (!query) return [];
    const response = await apiClient.get(
        `/api/v1/places/search?q=${encodeURIComponent(query)}`
    );
    const data = response.data;
    return Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
            ? data.items
            : [];
};

export const createPlan = async (payload: CreatePlanPayload): Promise<CreatePlanResponse> => {
    const response = await apiClient.post("/api/v1/planner/plan", payload);
    return response.data;
};

// Plan Details Types
export interface QuickInfo {
    rating?: {
        count: number;
        stars: number;
        display: string;
    };
    website?: {
        url: string;
        display: string;
    };
    location?: {
        display: string;
        latitude: number;
        longitude: number;
    };
    budget?: {
        label: string;
        level: string;
        display: string;
        description: string;
    };
    phone?: {
        number: string;
        display: string;
    };
}

export interface Activity {
    title: string;
    cid: string | null;
    attraction_name: string;
    latitude: number;
    longitude: number;
    rating: number | null;
    rating_count: number | null;
    activity: string;
    duration: string;
    price: string;
    is_free: boolean | null;
    why_special: string;
    best_time: string;
    insider_tip: string;
    map_location: string;
    difficulty: string;
    wheelchair_accessible: string;
    fitness_required: string;
    accessibility_notes: string | null;
    peak_hours: string;
    quietest_time: string;
    crowd_level: string;
    quick_info?: QuickInfo | null;
}

export interface TimeSlot {
    description: string;
    must_do: Activity[];
    side_activities: Activity[];
    weather_tips: string;
}

export interface DayNotes {
    logistics_tip: string;
    breakfast_suggestion: string;
    lunch_suggestion: string;
    dinner_suggestion: string;
}

export interface DailyWeather {
    forecast_date: string;
    temp_min: number;
    temp_max: number;
    feels_like_c: number;
    humidity: number;
    chance_rain: number;
    cloud_cover: number;
    precip_mm: number;
    wind_speed_kph: number;
    wind_dir: number;
    pressure_mb: number;
    visibility_km: number;
    moon_phase: number;
    sunrise: string;
    sunset: string;
    summary: string;
    condition: string;
    moon_phase_emoji: string;
    moon_phase_name: string;
    moon_phase_percent: number;
    weather_emoji: string;
    wind_direction: string;
    wind_direction_emoji: string;
}

export interface Day {
    day_count: number;
    day_date: string;
    day_display: string;
    title: string;
    morning: TimeSlot;
    afternoon: TimeSlot;
    evening: TimeSlot;
    notes: DayNotes;
    weather: DailyWeather;
}

export interface Itinerary {
    title: string;
    destination: string;
    days: Day[];
}

export interface GetIn {
    arrival_guide: string;
    quick_facts: { [key: string]: string };
    step_by_step_summary: string[];
    insider_tips: string[];
    common_mistakes: string[];
    money_saving_tips: string[];
    disclaimer: string;
    verified_date: string;
    source_urls: string[];
}

export interface PackingItem {
    item: string;
    quantity: string;
    reason: string;
}

export interface PackingRecommendations {
    essentials: PackingItem[];
    clothing: PackingItem[];
    extras: PackingItem[];
}

export interface UsefulLink {
    title: string;
    url: string;
}

export interface LocalEvent {
    name: string;
    when: string;
    where: string | null;
    url: string;
    note: string | null;
}

export interface Currency {
    code: string;
    eur_exchange_rate: string;
    name: string;
    rate_last_updated: string;
    symbol: string;
    usd_exchange_rate: string;
}

export interface EmergencyNumbers {
    ambulance: string;
    fire: string;
    police: string;
    universal: string;
    notes?: string | null;
}

export interface Language {
    code: string;
    name: string;
}

export interface Newspaper {
    language: string;
    name: string;
    url: string;
}

export interface SafetyIndices {
    global_peace_index: number;
    global_peace_rank: number;
    indices_year: number;
    risk_level: string;
    safety_level: string;
    safety_score: number;
    travelsafe_index: number;
    us_news_rank: number;
    travel_advisory_level?: string;
    advisory_summary?: string;
    common_risks?: string[];
    last_updated?: string;
}

export interface HealthAdvisories {
    vaccinations_required: string[];
    vaccinations_recommended: string[];
    malaria_risk: string | null;
    water_safety: string;
    medical_facilities: string;
    // Legacy fields (no longer returned by API)
    malaria_areas?: string;
    dengue_risk?: string;
    dengue_areas?: string | null;
    yellow_fever_risk?: string | null;
    zika_risk?: string | null;
    food_safety_concerns?: string[];
}

export interface BudgetBreakdown {
    accommodation_total: string;
    daily_expenses: {
        day_num: number;
        activities_cost: string;
        food_estimate: string;
        local_transport: string;
        daily_total: string;
    }[];
    grand_total_min: string;
    grand_total_max: string;
    per_person_per_day: string;
    currency_code: string;
    exchange_note: string;
    budget_tips: string[];
    disclaimer: string;
}

export interface Connectivity {
    wifi_availability: string;
    sim_recommendation: string; // JSON string in response
    estimated_cost: string; // JSON string in response
    essential_apps: string[];
    offline_tips: string; // JSON string in response
    language_barrier: string; // JSON string in response
}

export interface VisaInfo {
    required: boolean;
    types_available: string[];
    eligible_countries: string;
    duration_allowed: string;
    application_process: string;
    required_documents: string[];
    official_website: string;
}

export interface PermitInfo {
    name: string;
    required_for: string;
    where_to_obtain: string;
    advance_booking_required: boolean;
    notes: string;
}

export interface PermitsBookings {
    visa: VisaInfo;
    permits_required: PermitInfo[];
    must_book_ahead: any[];
    licensed_operators_required: boolean;
    licensed_operators: any[];
    restricted_areas: string[];
    entry_regulations: string[];
    disclaimer: string;
    verified_date: string;
    source_urls: string[];
}

export interface PhotoSpot {
    name: string;
    related_activity: string;
    best_time: string;
    photography_tip: string;
    sunrise_sunset_note?: string;
}

export interface LocalIntelligence {
    area_km2: number;
    calling_code: string;
    capital: string;
    country: string;
    country_code: string;
    country_code3: string;
    currency: Currency;
    destination: string;
    driving_side: string;
    emergency_numbers: EmergencyNumbers;
    flag_url: string;
    landlocked: boolean;
    local_newspapers: Newspaper[];
    mobile_operators: string[];
    neighboring_countries: string[];
    official_languages: Language[];
    population: number;
    region: string;
    safety_indices: SafetyIndices;
    start_of_week: string;
    subregion: string;
    timezone: string;
    health_advisories?: HealthAdvisories;
    national_symbol_url?: string;
    data_last_updated?: string;
}


export interface Preferences {
    destination: string;
    latitude: number;
    longitude: number;
    country_code: string;
    origin_country: string | null;
    passport_country: string | null;
    start_date: string;
    end_date: string;
    person_count: number;
    budget_type: string;
    travel_type: string[];
    duration: number;
    month: string;
    language: string;
}

export interface TimeMeta {
    start_time: string;
    end_time: string;
    duration_s: number;
}

export interface TokensMeta {
    ended_at: string;
    ended_ts: number;
    per_model: {
        [modelName: string]: {
            total: number;
            prompt_tokens: number;
            completion_tokens: number;
        };
    };
    duration_s: number;
    request_id: string;
    started_at: string;
    started_ts: number;
    grand_total: {
        total: number;
        prompt_tokens: number;
        completion_tokens: number;
    };
    per_operation: {
        [operationName: string]: {
            total: number;
            prompt_tokens: number;
            completion_tokens: number;
        };
    };
}

export interface WeatherContext {
    hint: string;
    by_day: { [date: string]: string };
    advice_by_day: { [date: string]: string };
}

export interface Plan {
    preferences: Preferences;
    get_in: GetIn;
    itinerary: Itinerary;
    packing_recommendations: PackingRecommendations;
    core_insights: string;
    useful_links: UsefulLink[];
    local_events: LocalEvent[];
    destination_images: string[];
    weather_context: WeatherContext;
    local_intelligence: LocalIntelligence;
    budget_breakdown: BudgetBreakdown;
    connectivity: Connectivity;
    permits_bookings: PermitsBookings;
    photo_spots: PhotoSpot[];
    time_meta: TimeMeta;
    tokens_meta: TokensMeta;
}

export type DestinationWarningType = "SEASONAL_CLOSURE" | "ATTRACTION_CLOSED" | "OTHER";
export type Severity = "high" | "medium" | "low";

export interface WarningDetails {
    reason: string;
    closed_months: string[] | null;
    open_months: string[] | null;
    travel_month: string | null;
    closed_dates: string[] | null;
    closed_days: string[];
    source: string;
}

export interface WarningSuggestions {
    recommended_months: string[] | null;
    alternatives: string[] | null;
    action: string;
}

export interface DestinationWarning {
    type: string;
    severity: Severity;
    icon: string;
    message: string;
    target: string;
    details: WarningDetails;
    suggestions: WarningSuggestions;
}

export interface PlanDetailsResponse {
    plan_id: string;
    slug: string;
    tags: string[];
    is_public: boolean;
    stream_url: string | null;
    destination_warnings: DestinationWarning[];
    plan: Plan;
}

export const getPlanDetails = async (slug: string): Promise<PlanDetailsResponse> => {
    const response = await apiClient.get(`/api/v1/plans/${slug}`);
    return response.data;
};
